import PropTypes from 'prop-types'
import React, { useState } from 'react'
import styled from 'styled-components'
import media from '../../utils/style'

const TabButton = styled('button')`
  position: relative;
  background-color: ${(props) => props.theme.colors.white};
  border: none;
  padding: 0 16px;
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  border-radius: 3px 3px 0 0;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  cursor: pointer;
  color: ${(props) => props.theme.colors.grey_light};

  &:hover {
    height: 45px;
    transition: all 0.3s ease;
    color: ${(props) => props.theme.colors.grey_op80};
  }

  &[data-active='true'] {
    z-index: 1;
    height: 50px;
    transition: all 0.3s ease;
    color: ${(props) => props.theme.colors.blue};
    & span:after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 50%;
      transform: translateX(-50%);
      width: 60%;
      height: 3px;
      background-color: ${(props) => props.theme.colors.blue};
    }
  }

  ${media.MediumScreenUp`
    padding: 0 32px;
  `}
`

const TabButtonGroup = styled('div')`
  display: flex;
  align-items: flex-end;
  gap: 3px;
  min-height: 50px;
  overflow-x: auto;
`

const TabContentWrapper = styled('div')`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  box-shadow: 0 -10px 6px -10px rgba(0, 0, 0, 0.1);
`

const ContentTabsPanelWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 100%;
  & [data-value='${(props) => props.activeItem}'] {
    display: block;
  }
`

const ContentTabsPanelItemWrapper = styled('div')`
  display: none;
`

export const ContentTabsPanel = ({ children, items }) => {
  const [activeItem, setActiveItem] = useState(items[0].value)

  return (
    <ContentTabsPanelWrapper
      activeItem={activeItem}
      role="tablist"
      aria-label="Content tabs"
    >
      <TabButtonGroup role="presentation">
        {items.map((item) => (
          <TabButton
            onClick={() => setActiveItem(item.value)}
            key={item.value}
            data-active={activeItem === item.value}
            role="tab"
            aria-selected={activeItem === item.value}
            aria-controls={`panel-${item.value}`}
            id={`tab-${item.value}`}
          >
            <span>{item.label}</span>
          </TabButton>
        ))}
      </TabButtonGroup>
      <TabContentWrapper>{children}</TabContentWrapper>
    </ContentTabsPanelWrapper>
  )
}

export const ContentTabsPanelItem = ({ children, value }) => {
  return (
    <ContentTabsPanelItemWrapper
      data-value={value}
      role="tabpanel"
      id={`panel-${value}`}
      aria-labelledby={`tab-${value}`}
      tabIndex={0}
    >
      {children}
    </ContentTabsPanelItemWrapper>
  )
}

ContentTabsPanel.displayName = 'ContentTabsPanel'

ContentTabsPanel.propTypes = {
  children: PropTypes.node.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired
}

ContentTabsPanelItem.displayName = 'ContentTabsPanelItem'

ContentTabsPanelItem.propTypes = {
  value: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired
}
